import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, User as UserIcon, Eye, EyeOff, ArrowRight, Loader2, Globe, Shield, Languages } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { errMsg } from "@/lib/format";
import api from "@/lib/api";
import MoneyTree from "@/components/MoneyTree";
import { trackSignupStarted, trackSignupCompleted, getStoredClickIds } from "@/lib/tracking";

const PLAN_COPY = {
  pro_special: { headline: "$1 · 7 días.", subtitle: "Acceso completo. Día 7: $4 (Pro). Luego $5/mes. Cancela cuando quieras." },
  family_special: { headline: "$1 · 7 días.", subtitle: "Acceso completo. Día 7: $8 (Familia). Luego $9/mes. Cancela cuando quieras." },
  pro_monthly: { headline: "Plan Pro.", subtitle: "$5/mes. Cancela cuando quieras." },
  family_monthly: { headline: "Plan Familia.", subtitle: "$9/mes, hasta 4 miembros. Cancela cuando quieras." },
  lifetime_offer: { headline: "Acceso de por vida.", subtitle: "$100 pago único (oferta de lanzamiento). Sin renovaciones, nunca más cobros." },
  lifetime_regular: { headline: "Acceso de por vida.", subtitle: "$250 pago único. Sin renovaciones, nunca más cobros." },
};

const COUNTRIES = [
  { code: "DO", name: "República Dominicana", currency: "DOP" },
  { code: "US", name: "Estados Unidos", currency: "USD" },
  { code: "MX", name: "México", currency: "MXN" },
  { code: "CO", name: "Colombia", currency: "COP" },
  { code: "AR", name: "Argentina", currency: "ARS" },
  { code: "CL", name: "Chile", currency: "CLP" },
  { code: "PE", name: "Perú", currency: "PEN" },
  { code: "BR", name: "Brasil", currency: "BRL" },
  { code: "ES", name: "España", currency: "EUR" },
  { code: "VE", name: "Venezuela", currency: "VES" },
  { code: "EC", name: "Ecuador", currency: "USD" },
  { code: "GT", name: "Guatemala", currency: "GTQ" },
  { code: "PA", name: "Panamá", currency: "USD" },
  { code: "CR", name: "Costa Rica", currency: "CRC" },
];

export default function AuthPage({ mode = "login" }) {
  const { login, register } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: "", password: "", name: "", country_code: "DO", country_currency: "DOP" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [geoDetected, setGeoDetected] = useState(null);

  const isLogin = mode === "login";
  const plan = new URLSearchParams(loc.search).get("plan") || "pro_special";
  const planCopy = PLAN_COPY[plan] || PLAN_COPY.pro_special;

  useEffect(() => {
    if (isLogin) return;
    api.get("/geo/detect").then(r => {
      const cc = (r.data?.country_code || "DO").toUpperCase();
      const found = COUNTRIES.find(c => c.code === cc);
      if (found) {
        setForm(f => ({ ...f, country_code: found.code, country_currency: found.currency }));
        setGeoDetected({ name: r.data?.country_name, currency: found.currency });
      }
    }).catch(() => {});
  }, [isLogin]);

  // Funnel event 3 — fires once when the register form (not login) mounts.
  useEffect(() => {
    if (!isLogin) trackSignupStarted(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        nav("/", { replace: true });
      } else {
        await register(form.email, form.password, form.name, {
          country_code: form.country_code,
          country_currency: form.country_currency,
          ...getStoredClickIds(),
        });
        // Funnel event 4 — real account created. Ad-click ids just went to the
        // backend with this same call, so the server-side day-7/converted
        // events (fired from the Stripe webhook) can be tied back to them.
        trackSignupCompleted(plan, form.country_code);
        // Binary paid gate: after registration go straight to the value screen
        // that starts the checkout with the plan chosen on the landing page (?plan=...).
        nav(`/activar?plan=${encodeURIComponent(plan)}`, { replace: true });
      }
    } catch (e2) {
      setErr(errMsg(e2, "No se pudo continuar"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Backend owns the whole OAuth2 code flow (own Google Cloud project) and
    // redirects back to PUBLIC_APP_URL once done — nothing to build here.
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
  };

  return (
    <div className="relative min-h-screen bg-[#0b0a10] text-white overflow-hidden">
      {/* SCARCITY BAR — same as landing for visual continuity */}
      <div className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-violet-500 text-white text-center py-2 text-xs sm:text-sm font-medium relative z-30">
        {t("scarcity.bar")}
      </div>

      {/* Minimal top nav */}
      <header className="relative z-30 px-5 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="auth-brand">
          <img src="/logo.png" alt="The Trading Dock" className="w-9 h-9 rounded-xl ring-1 ring-white/10" />
          <div className="leading-tight">
            <div className="text-[13px] font-bold tracking-tight text-white">THE TRADING DOCK<sup className="text-[8px] opacity-60">®</sup></div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-semibold">finance</div>
          </div>
        </Link>
        <button
          onClick={() => setLang(lang === "es" ? "en" : "es")}
          data-testid="auth-lang-toggle"
          className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-white/10 hover:bg-white/5 px-2.5 py-1.5 rounded-lg font-semibold uppercase"
        >
          <Languages className="w-3.5 h-3.5" /> {lang === "es" ? "EN" : "ES"}
        </button>
      </header>

      {/* SPLIT LAYOUT — uses full width */}
      <div className="relative grid lg:grid-cols-2 min-h-[calc(100vh-110px)]">
        {/* LEFT — visual brand / hero side (fintech blocks + money tree) */}
        <aside className="relative hidden lg:flex flex-col justify-between px-12 xl:px-20 py-14 overflow-hidden border-r border-white/10 bg-fintech-blocks bg-blocks-overlay" data-testid="auth-visual-side">

          {/* Content (top) — brand */}
          <div className="relative z-10 max-w-xl">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="The Trading Dock" className="w-14 h-14 rounded-2xl ring-1 ring-white/20 shadow-[0_10px_40px_-8px_rgba(0,0,0,0.5)]" />
              <div>
                <div className="text-xl xl:text-2xl font-black tracking-tight text-white">THE TRADING DOCK<sup className="text-[10px] opacity-70 ml-1">®</sup></div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-white/70 font-bold mt-0.5">FINANCE · SINCE 2026</div>
              </div>
            </div>
          </div>

          {/* Content (center) — Money tree + tagline */}
          <div className="relative z-10 flex flex-col items-center text-center my-2" data-testid="auth-money-tree">
            <MoneyTree className="w-[300px] xl:w-[360px] drop-shadow-[0_15px_45px_rgba(167,139,250,0.45)]" />
            <h2 className="mt-6 text-3xl xl:text-4xl font-black leading-[1.05] tracking-tight text-white max-w-md">
              Cultiva tu <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-fuchsia-300 to-cyan-300">árbol financiero.</span>
            </h2>
            <p className="mt-3 text-sm text-white/75 max-w-md">
              Multi-divisa, OCR, deudas y conciliación. Todo lo que tu dinero necesita para crecer.
            </p>
          </div>

          {/* Content (bottom) — stat strip + trust line */}
          <div className="relative z-10">
            <div className="grid grid-cols-3 gap-5 border-t border-white/15 pt-6 mb-5">
              <Stat value="9+" label="Divisas en vivo" />
              <Stat value="3s" label="OCR por recibo" />
              <Stat value="$5" label="USD / mes" />
            </div>
            <div className="flex items-center gap-2 text-[11px] text-white/65">
              <Shield className="w-3.5 h-3.5" /> JWT · bcrypt · Stripe · cancela cuando quieras
            </div>
          </div>
        </aside>

        {/* RIGHT — clean dark side with form */}
        <main className="relative flex flex-col justify-center px-5 sm:px-10 lg:px-16 xl:px-24 py-10 lg:py-0 bg-[#0b0a10] overflow-hidden">
          {/* Subtle accent matching the fintech blocks palette */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-[-25%] right-[-15%] w-[500px] h-[500px] rounded-full bg-violet-600/12 blur-[160px]" />
            <div className="absolute bottom-[-15%] left-[-10%] w-[400px] h-[400px] rounded-full bg-indigo-500/10 blur-[140px]" />
          </div>
          <div className="relative z-10 w-full max-w-md mx-auto lg:mx-0">
            {/* Greeting */}
            <div className="mb-7">
              <div className="text-[10px] uppercase tracking-[0.28em] text-violet-300 font-bold" data-testid="auth-eyebrow">
                {isLogin ? "ACCESO" : "REGISTRO"}
              </div>
              <h1 className="mt-2 text-3xl sm:text-4xl xl:text-[44px] font-black tracking-tight leading-[1.05]" data-testid="auth-title">
                {isLogin ? (
                  <>Hola otra vez.<br /><span className="brand-mark">Continúa.</span></>
                ) : (
                  <>Empieza por<br /><span className="brand-mark">{planCopy.headline}</span></>
                )}
              </h1>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                {isLogin ? "Tu panel financiero te espera." : planCopy.subtitle}
              </p>
            </div>

            {/* Dark-glass fintech form card — matches the rest of the funnel (#0b0a10) */}
            <form onSubmit={submit} className="rounded-3xl bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)] p-6 sm:p-7 space-y-3.5" data-testid="auth-form">
              {!isLogin && (
                <Field icon={UserIcon} label="Nombre">
                  <input data-testid="auth-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Tu nombre" className="w-full bg-transparent outline-none text-white placeholder:text-slate-500 text-sm" />
                </Field>
              )}
              {!isLogin && (
                <Field icon={Globe} label={geoDetected ? `País · detectado: ${geoDetected.name}` : "País"}>
                  <select data-testid="auth-country-select" value={form.country_code} onChange={(e) => { const c = COUNTRIES.find(x => x.code === e.target.value); setForm({ ...form, country_code: e.target.value, country_currency: c?.currency || "USD" }); }} className="w-full bg-transparent outline-none text-white text-sm cursor-pointer appearance-none">
                    {COUNTRIES.map(c => <option key={c.code} value={c.code} className="bg-[#14131b] text-white">{c.name} ({c.currency})</option>)}
                  </select>
                </Field>
              )}
              <Field icon={Mail} label="Correo">
                <input data-testid="auth-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="tu@correo.com" className="w-full bg-transparent outline-none text-white placeholder:text-slate-500 text-sm" />
              </Field>
              <Field icon={Lock} label="Contraseña">
                <input data-testid="auth-password-input" type={show ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} placeholder="••••••••" className="w-full bg-transparent outline-none text-white placeholder:text-slate-500 text-sm" />
                <button type="button" onClick={() => setShow(!show)} className="text-slate-400 hover:text-white" data-testid="auth-password-toggle">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </Field>

              {isLogin && (
                <div className="flex justify-end -mt-1">
                  <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-[12px] text-violet-300 hover:text-violet-200 font-semibold">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              )}

              {err && <div data-testid="auth-error" className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{err}</div>}

              <button type="submit" disabled={loading} data-testid="auth-submit-btn" className="w-full mt-2 flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white font-semibold py-3.5 rounded-xl transition-all shadow-[0_10px_40px_-10px_rgba(167,139,250,0.6)] disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                  {isLogin ? "Iniciar sesión" : "Crear cuenta"}
                  <ArrowRight className="w-4 h-4" />
                </>}
              </button>

              <div className="relative my-1" data-testid="auth-divider">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                <div className="relative flex justify-center"><span className="bg-[#14131b] px-3 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">o</span></div>
              </div>

              <button type="button" onClick={handleGoogleLogin} data-testid="auth-google-btn" className="w-full flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 text-white font-semibold py-3 rounded-xl transition-all border border-white/15">
                <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuar con Google
              </button>
            </form>

            {/* Switch mode link — bottom, NOT top-right */}
            <p className="mt-6 text-center text-sm text-slate-400" data-testid="auth-switch-row">
              {isLogin ? "¿Aún no tienes cuenta? " : "¿Ya tienes cuenta? "}
              <Link
                to={isLogin ? "/register" : "/login"}
                state={loc.state}
                data-testid="auth-switch-mode"
                className="text-violet-300 hover:text-violet-200 font-semibold"
              >
                {isLogin ? "Empieza por $1 →" : "Iniciar sesión →"}
              </Link>
            </p>

            {/* PWA hint - subtle */}
            <div className="mt-5 text-center text-[11px] text-slate-500" data-testid="pwa-install-hint">
              📱 Disponible como PWA · Safari iPhone → Compartir → &ldquo;Añadir a inicio&rdquo;
            </div>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-4 text-center text-[11px] text-slate-600 border-t border-white/5">
        © 2026 Pryma Wellness Group Inc. · The Trading Dock®
      </footer>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold mb-1.5">{label}</div>
      <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 focus-within:border-violet-400 focus-within:bg-white/[0.06] focus-within:ring-2 focus-within:ring-violet-500/40 rounded-xl px-4 py-3 transition-all">
        <Icon className="w-4 h-4 text-slate-400" />
        {children}
      </div>
    </label>
  );
}

function Stat({ value, label }) {
  return (
    <div data-testid={`auth-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-3xl xl:text-4xl font-black tracking-tight text-white tabular-nums">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
    </div>
  );
}
